<script lang="ts">
    import { getContext, onMount, tick } from 'svelte'
    import { derived as sderived, writable, type Readable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'

    import type { HydriaDocument, IAttachment, IImage } from 'hydria/HydriaDocument'
    // import Dropzone from 'hydria/components/Dropzone.svelte'
    import Dropzone from 'svelte-file-dropzone'
    import HumanizedBytes from 'hydria/components/HumanizedBytes.svelte'
    import Modal from 'hydria/components/Modal.svelte'
    import { gettext as _, resolve, tgettext, type TGettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { makeSortString } from 'hydria/utils/strings'

    import VerticalFieldWidget from './VerticalFieldWidget.svelte'
    import { selectSrc } from 'hydria/components/component_helpers'

    interface Props {
        saved: string | undefined
        value: string | undefined
        disabled?: boolean
        allowEmpty?: boolean
        previewName?: string
        label?: TGettext
        simpleMode?: boolean
        Document?: HydriaDocument | null
        save?: (v) => void
        isValid?: (v) => void
        isPristine?: (v) => void
    }

    let { saved: savedValue, value, disabled = false, allowEmpty = false, previewName = 'admin', label = tgettext('Image'), simpleMode = false, Document = null, save = (v) => {}, isValid: onIsValid = (v) => {}, isPristine: onIsPristine = (v) => {} }: Props = $props()

    let allowedMimetypes: Writable<Set<string>> = $state()

    let images: Readable<IImage[]> = $state()
    if (Document) {
        images = sderived(Document.attachedImages, ($atts) => $atts)
        allowedMimetypes = Document.allowedImageMimetypes
    } else {
        const attachments: Readable<IAttachment[]> = getContext('attachments')
        images = sderived(attachments, ($atts) => ($atts ? ($atts.filter((att) => att.mimetype.startsWith('image/')) as IImage[]) : []))
        allowedMimetypes = writable(new Set())
    }

    interface LImage extends IImage {
        _type: 'image'
    }

    const imageIds = sderived(images, ($atts) => new Set($atts.map((att) => att.id)))

    let selectedAtt: IAttachment | undefined = $derived(value && $imageIds.has(value) ? $images.filter((att) => att.id === value)[0] : undefined)

    let maxImages = $derived(allowEmpty ? 8 : 9)

    let isValid: boolean = $derived.by(() => {
        let lisValid = (value != null && $imageIds.has(value)) || (value == null && allowEmpty)
        tick().then(() => {
            onIsValid(lisValid)
        })
        return lisValid
    })

    let isPristine: boolean = $derived.by(() => {
        let lisPristine = value === savedValue
        tick().then(() => {
            onIsPristine(lisPristine)
        })
        return lisPristine
    })

    function keep(newValue: string | null) {
        // value = newValue
        save(newValue)
        open = false
    }

    let open = $state(false)

    // #############################################################  Upload stuff
    interface LUpload {
        _type: 'upload'
        file: File
        progress: number
        started: DateTime
        ended?: DateTime
        status: 'uploading' | 'aborted' | 'finished' | 'error'
        xhr: XMLHttpRequest
        // formElement: HTMLFormElement
        // formData: FormData
        previewImage?: string
    }
    let uploads: Writable<LUpload[]> = writable([])
    let imagesAndUploads: Readable<Array<LUpload | LImage>> = sderived([images, uploads], ([$images, $uploads]) => {
        return orderBy(
            [
                ...$images.map((i) => {
                    ;(i as LImage)._type = 'image'
                    return i as LImage
                }),
                ...$uploads,
            ],
            [
                (i: LImage | LUpload) => {
                    return makeSortString(i._type === 'upload' ? i.file.name : i.filename)
                },
            ],
            ['asc'],
        )
    })

    onMount(() => {
        const interval = setInterval(() => {
            uploads.update(($uploads) => {
                return $uploads.filter((upload) => upload.status == 'uploading' || !upload.ended || upload.ended > DateTime.now().minus({ seconds: 5 }))
            })
        }, 250)

        return () => {
            clearInterval(interval)
        }
    })

    function abort(upload: LUpload) {
        if (upload.status == 'uploading') {
            upload.xhr.abort()
        }
    }

    function setupFileUpload(file: File) {
        // const formElement = document.createElement('form')
        const upload: LUpload = {
            _type: 'upload',
            file: file,
            progress: 0,
            status: 'uploading',
            started: DateTime.now(),
            xhr: new XMLHttpRequest(),
            // formElement: formElement,
            // formData: new FormData(formElement),
            previewImage: undefined,
        }
        uploads.update(($uploads) => {
            $uploads.push(upload)
            return $uploads
        })
        const formData = new FormData()
        formData.append('file', file)

        const xhr = upload.xhr

        if (['image/jpeg', 'image/avif', 'image/webp', 'image/svg+xml', 'image/png'].includes(file.type)) {
            const reader = new FileReader()
            reader.onload = (e: any) => {
                upload.previewImage = e.target.result as string
                // To trigger reactivity :|
                uploads.update(($uploads) => $uploads)
            }
            reader.readAsDataURL(file)
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState == XMLHttpRequest.DONE) {
                let resp: any = {
                    status: xhr.status,
                    data: null,
                }
                if (xhr.responseText) {
                    try {
                        resp.data = JSON.parse(xhr.responseText)
                    } catch (e) {}
                }

                upload.ended = DateTime.now()

                if (resp.status !== 200) {
                    upload.status = 'error'
                } else {
                    upload.status = 'finished'
                    if (Document) Document.updateAttachmentList(resp.data.attachments, resp.data.newly_uploaded_id)
                }
            }
        }
        function remove() {
            uploads.update(($uploads) => {
                const i = $uploads.indexOf(upload)
                if (i >= 0) $uploads.splice(i, 1)
                return $uploads
            })
        }
        xhr.upload.addEventListener(
            'load',
            (e) => {
                upload.progress = 100
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'progress',
            (e) => {
                upload.progress = Math.round((e.loaded * 100) / e.total)
            },
            false,
        )
        xhr.upload.addEventListener(
            'error',
            (e) => {
                upload.status = 'error'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'abort',
            (e) => {
                upload.status = 'aborted'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        // @ts-expect-error
        xhr.open('POST', `/json/cms/1/upload/${Document.docType}/${Document.docId}`, true)
        xhr.setRequestHeader('X-Lg', $lg)
        // @ts-expect-error
        xhr.setRequestHeader('X-Hydria-Token', session.token)
        xhr.setRequestHeader('Accept', 'application/json')
        xhr.send(formData)
    }

    function handleFile(e: any) {
        if (!e.detail.acceptedFiles.length) return

        for (const file of e.detail.acceptedFiles as File[]) {
            setupFileUpload(file)
        }
    }
</script>

<style>
    img {
        width: 100%;
        height: 4rem;
        object-fit: contain;
    }

    .img-wrap {
        flex: 1 0 0;
        display: grid;
        justify-content: center;
        align-content: center;
    }

    .img-button {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 0.25rem;
        max-width: 100%;
        overflow: hidden;
    }
    .img-desc {
        font-size: 0.75em;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .up-progress {
        display: grid;
        grid-template-columns: 2fr 1fr auto;
        gap: 0.25rem 0.5rem;
        align-items: baseline;
    }
    .up-progress > :nth-child(3) {
        justify-self: end;
    }
</style>

{#if simpleMode}
    {#if selectedAtt == null}
        <button type="button" class="btn btn-outline-secondary" {disabled} onclick={() => (open = true)}>
            <em>{$_('Aucune image')}</em>
        </button>
    {:else}
        <button type="button" class="btn btn-outline-secondary" {disabled} onclick={() => (open = true)} data-tooltip={selectedAtt.filename}>
            <span class="img-wrap"><img class="checkered" src={selectSrc(selectedAtt, previewName).url} alt={selectedAtt.filename} width={selectSrc(selectedAtt, previewName).width} height={selectSrc(selectedAtt, previewName).height} /></span>
        </button>
    {/if}
{:else}
    <VerticalFieldWidget {label} {isPristine} {isValid}>
        {#if selectedAtt == null}
            <button type="button" class="btn btn-outline-secondary" {disabled} onclick={() => (open = true)}>
                <em>{$_('Aucune image')}</em>
            </button>
        {:else}
            <button type="button" class="btn btn-outline-secondary" {disabled} onclick={() => (open = true)} data-tooltip={selectedAtt.filename}>
                <span class="img-wrap"><img class="checkered" src={selectSrc(selectedAtt, previewName).url} alt={selectedAtt.filename} width={selectSrc(selectedAtt, previewName).width} height={selectSrc(selectedAtt, previewName).height} /></span>
            </button>
        {/if}
    </VerticalFieldWidget>
{/if}

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape' && open) open = false
    }}
/>

{#if open}
    <Modal close={() => (open = false)} modalClasses={$images.length > maxImages ? 'modal-lg' : ''}>
        <div class="modal-header">
            <h5>{$resolve(label)}</h5>
        </div>
        <div class="modal-body">
            <div class="container">
                <div class="row" class:row-cols-4={$images.length > maxImages} class:row-cols-3={$images.length <= maxImages}>
                    {#if allowEmpty}
                        <div class="d-grid p-1">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                class:active={value == null}
                                onclick={() => {
                                    keep(null)
                                }}
                            >
                                <em>{$_('Aucune image')}</em>
                            </button>
                        </div>
                    {:else if !$images.length}
                        <div class="d-grid p-1">
                            <em>{$_('Aucune image')}</em>
                        </div>
                    {/if}
                    {#each $images as att}
                        <div class="d-grid p-1">
                            <button
                                type="button"
                                class="btn btn-outline-secondary img-button"
                                class:active={value === att.id}
                                onclick={() => {
                                    keep(att.id)
                                }}
                            >
                                <span class="img-wrap"><img class="checkered" src={selectSrc(att, previewName).url} alt={att.filename} /></span>
                                <span class="img-desc">{att.filename}</span>
                            </button>
                        </div>
                    {/each}
                </div>
            </div>
        </div>

        {#if $uploads.length}
            <div class="modal-footer">
                <div class="up-progress">
                    {#each $uploads as upload}
                        <div>
                            <span>{upload.file.name}</span>
                            <span><HumanizedBytes value={upload.file.size} /></span>
                        </div>
                        <div>
                            <div class="progress mb-0">
                                {#if upload.status == 'uploading'}
                                    <div class="progress-bar progress-bar-striped progress-bar-animated" style="min-width: 3em; width: {upload.progress}%" role="progressbar" aria-valuenow={upload.progress} aria-valuemin={0} aria-valuemax={100}>
                                        {upload.progress} %
                                    </div>
                                {:else if upload.status == 'aborted'}
                                    <div class="progress-bar bg-warning" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>
                                        {$_('Cancelled')}
                                    </div>
                                {:else if upload.status == 'error'}
                                    <div class="progress-bar bg-danger" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>
                                        {$_('Error')}
                                    </div>
                                {:else if upload.status == 'finished'}
                                    <div class="progress-bar bg-success" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>100 %</div>
                                {/if}
                            </div>
                        </div>
                        <div>
                            {#if upload.status == 'uploading'}
                                <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => abort(upload)}>{$_('Cancel')}</button>
                                <!-- {:else if upload.status == 'finished'}
                        <button type="button" class="btn btn-success btn-xs" disabled>{$_('Finished')}</button>
                    {:else if upload.status == 'aborted'}
                        <button type="button" class="btn btn-warning btn-xs" disabled>{$_('Cancelled')}</button>
                    {:else if upload.status == 'error'}
                        <button type="button" class="btn btn-danger btn-xs" disabled>{$_('Error')}</button> -->
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}

        <div class="modal-footer">
            {#if Document}
                <div class="upload-zone">
                    <Dropzone on:drop={handleFile} accept={[...$allowedMimetypes].join(',')} multiple={true}>
                        <div class="d-flex gap-2">
                            <div><Icon inline icon="mdi:image-size-select-actual" /></div>
                            <div class="col">
                                {$_('Cliquer ou glisser/déposer des images.')}
                            </div>
                        </div>
                    </Dropzone>
                </div>
                <div class="col"></div>
            {/if}
            <button type="button" class="btn btn-outline-secondary" onclick={() => (open = false)}>{$_('Fermer')}</button>
        </div>
    </Modal>
{/if}
